// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { Dispatch, SetStateAction, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { CHAT_DEFAULT_WIDTH } from 'features/assetControl/assetControl.constants';
import { StatusDot } from 'greener-ui-library';
import { Icons } from 'greener-ui-library';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useDialogue } from 'hooks/useChatSocket';
import { useLocalStorage } from 'hooks/useLocalStorage/useLocalStorage';
import { v4 as uuidv4 } from 'uuid';
import { chatReducer } from './chatReducer';
import { ChatHistory } from './components/ChatHistory';
import { ChatInputArea } from './components/ChatInputArea';
import { Message } from './components/Message';
import { ResizeHandle } from './components/ResizeHandle';
import { ChatAction, ClientCapability, MessageType, OperatorMessage } from './operatorChat.types';

const Counter = ({ count }: { count: number }) => (
  <Box
    sx={{
      width: 22,
      minWidth: 22,
      height: 22,
      backgroundColor: 'primary.main',
      color: 'white',
      borderRadius: '2px',
      display: 'flex',
      justifyContent: 'center',
      fontSize: 14,
      mx: 1,
    }}
    data-testid="unseen-count"
  >
    {count}
  </Box>
);

export const OperatorChat = ({
  unit,
  isOnline,
  isChatSplit,
  setIsChatSplit,
  isOpen,
  setIsOpen,
}: {
  unit: { externalIdentifier: string; name: string };
  isOnline: boolean;
  isChatSplit: boolean;
  setIsChatSplit: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [unseenMessageCount, setUnseenMessageCount] = useState(0);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [state, dispatch] = useReducer(chatReducer, {
    messages: [],
    menu: [],
  });
  const prevMessagesLength = useRef(state.messages.length);
  const xPadding = 2;
  const yPadding = 1.5;
  const drawerDefaultWidth = CHAT_DEFAULT_WIDTH;
  const drawerDefaultHeight = 555;
  const headerHeight = 56;
  const loadMoreTriggerPositionTop = 0;

  const [isChatSplitPreferred, setIsChatSplitPreferred] = useLocalStorage<boolean>('isChatSplitPreferred', false);

  const chatLoadMoreRef = useRef<() => void>();
  const chatContentRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsChatSplit(isChatSplitPreferred);
    }
  }, [isOpen, isChatSplitPreferred, setIsChatSplit]);

  const { sendMessage } = useDialogue({
    unitId: unit?.externalIdentifier,
    capabilities: [ClientCapability.TEXT_INPUT, ClientCapability.KEYBOARD],
    onMessage: dispatch,
  });

  const scrollToLastMessage = useCallback(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  const onChatContentScroll = useCallback(() => {
    const container = chatContentRef.current;
    if (!container) {
      return;
    }
    if (container.scrollTop === loadMoreTriggerPositionTop && scrollPositionRef.current === null) {
      scrollPositionRef.current = container.scrollHeight - container.scrollTop;
      chatLoadMoreRef.current?.();
    }
  }, []);

  const restoreScrollPosition = useCallback(() => {
    const container = chatContentRef.current;
    if (!container || scrollPositionRef.current === null) {
      return;
    }
    container.scrollTop = container.scrollHeight - scrollPositionRef.current;
    scrollPositionRef.current = null;
  }, []);

  useEffect(() => {
    scrollToLastMessage();
  }, [isOpen, state.messages]);

  const showOnlineStatusMessage = () => {
    dispatch({
      message_type: MessageType.UI_INFO,
      text: isOnline ? t('unitControl:chat.online') : t('unitControl:chat.offline'),
      timestamp: new Date().toISOString(),
    });
  };

  useEffect(() => {
    showOnlineStatusMessage();
  }, [isOnline]);

  useEffect(() => {
    dispatch({ action: ChatAction.RESET_CHAT } as OperatorMessage);
    showOnlineStatusMessage();
  }, [unit.externalIdentifier]);

  const onButtonClick = useCallback(
    (targetMessage: string, buttonData: string) => {
      sendMessage({
        uuid: uuidv4(),
        message_type: MessageType.BUTTON_PRESS,
        target_message: targetMessage,
        button_data: buttonData,
      });
      chatInputRef.current?.focus();
    },
    [sendMessage]
  );

  const onMenuCommandClick = useCallback(
    (command: string) => {
      sendMessage({
        uuid: uuidv4(),
        message_type: MessageType.COMMAND,
        text: command,
      });
      chatInputRef.current?.focus();
    },
    [sendMessage]
  );

  const handleLoadMore = useCallback((fn: () => void) => {
    chatLoadMoreRef.current = fn;
  }, []);

  const hasBackdrop = isOpen && isMobile;

  useEffect(() => {
    const isNewIncomingMessage =
      state.messages.length > prevMessagesLength.current &&
      [MessageType.EDIT, MessageType.INFORMATIONAL, MessageType.REPLY, MessageType.INPUT, MessageType.COMMAND].includes(
        state.messages[state.messages.length - 1]?.message_type
      );

    if (isNewIncomingMessage && !isOpen) {
      setUnseenMessageCount((prev) => prev + 1);
    }

    if (isOpen) {
      setUnseenMessageCount(0);
    }

    prevMessagesLength.current = state.messages.length;
  }, [state.messages.length, isOpen]);

  const [drawerWidth, setDrawerWidth] = useLocalStorage<number>('operatorChatDrawerWidth', drawerDefaultWidth);
  const [drawerHeight, setDrawerHeight] = useLocalStorage<number>('operatorChatDrawerHeight', drawerDefaultHeight);

  const handleResize = ({ deltaX, deltaY }: { deltaX: number; deltaY: number }) => {
    setDrawerWidth((prev) => Math.max(drawerDefaultWidth, Math.min(800, prev - deltaX)));
    setDrawerHeight((prev) => Math.max(300, Math.min(window.innerHeight, prev - deltaY)));
  };

  const handleSplitToggle = () => {
    setIsChatSplit(!isChatSplit);
    setIsOpen(true);
    setIsChatSplitPreferred(!isChatSplit);
  };

  const handleChatOpenToggle = () => {
    setIsOpen(!isChatSplit && !isOpen);
    if (isChatSplit) {
      setIsChatSplit(false);
    }
  };

  return (
    <Drawer
      anchor={isChatSplit ? 'right' : 'bottom'}
      open={true}
      onClose={() => setIsOpen(false)}
      hideBackdrop={!hasBackdrop}
      ModalProps={{
        keepMounted: true,
        disableEnforceFocus: true,
        disableScrollLock: true,
        sx: {
          pointerEvents: hasBackdrop ? 'auto' : 'none',
          zIndex: (theme) => theme.zIndex.drawer,
        },
      }}
      PaperProps={{
        sx: {
          width: isChatSplit ? drawerDefaultWidth : isMobile ? '100%' : drawerWidth,
          height: isChatSplit && !isMobile ? `calc(100% - ${headerHeight}px)` : isOpen ? drawerHeight : 58,
          top: isChatSplit && !isMobile ? headerHeight : 'auto',
          bottom: 0,
          left: 'auto',
          right: isChatSplit ? 0 : 27,
          background: (theme) => theme.palette.background.lightGrey,
          borderRadius: isChatSplit && !isMobile ? '0' : '8px 8px 0 0',
          boxShadow: '0px -4px 16px 0px rgba(0,0,0,0.18)',
          transition: 'height 0.3s ease',
          overflow: 'hidden',
          pointerEvents: 'auto',
          zIndex: (theme) => theme.zIndex.drawer,
          ...(isMobile
            ? {
                height: isOpen ? 'calc(100% - 70px)' : 58,
                width: '100%',
                right: 0,
              }
            : {}),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          py: yPadding,
          px: xPadding,
          background: (theme) => theme.palette.background.paper,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        onClick={() => {
          setIsOpen(!isOpen);
          if (isChatSplit) {
            setIsChatSplit(false);
          }
        }}
      >
        <Icons.ChatBubbleOutline sx={{ mr: 1 }} />
        <Typography
          variant="h4"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mr: 1,
          }}
        >
          {unit.externalIdentifier} | {unit.name}
        </Typography>
        <StatusDot color={isOnline ? 'primary.main' : 'grey.300'} sx={{ mr: 1 }} />
        <Box sx={{ flexGrow: 1 }} />
        {unseenMessageCount > 0 && <Counter count={unseenMessageCount} />}
        {!isMobile && isOpen && (
          <IconButton
            data-testid="split-button"
            onClick={(e) => {
              e.stopPropagation();
              handleSplitToggle();
            }}
            sx={{
              transition: 'transform 0.2s',
              transform: `rotate(${isChatSplit ? 90 : 0}deg)`,
              color: 'grey.700',
              mr: 1,
            }}
          >
            <Icons.SplitScreen />
          </IconButton>
        )}
        <IconButton data-testid="collapse-button" onClick={handleChatOpenToggle}>
          <Icons.ExpandMore
            sx={{
              transition: 'transform 0.2s',
              transform: `rotate(${isOpen && isChatSplit ? 0 : isOpen ? 0 : 180}deg)`,
              color: 'grey.700',
            }}
          />
        </IconButton>
      </Box>

      {isOpen && (
        <>
          <Box
            data-testid="chat-content"
            ref={chatContentRef}
            sx={{
              height: `calc(100% - 58px)`,
              overflowY: 'auto',
              overflowX: 'hidden', // after ws reconnect horizontal scrollbar appears, this fixes it
              pt: yPadding,
              px: xPadding,
              WebkitOverflowScrolling: 'touch', // enables momentum scroll on iOS
              overscrollBehavior: 'contain', // prevents background scroll
            }}
            onScroll={onChatContentScroll}
          >
            <ChatHistory
              unitId={unit.externalIdentifier}
              onRender={scrollToLastMessage}
              onLoadMore={handleLoadMore}
              onHistoryLoaded={restoreScrollPosition}
            />
            {state.messages.map((m: OperatorMessage, i: number) => (
              <Box key={`${m.uuid}${i}`}>
                <Message
                  lastMessageRef={i === state.messages.length - 1 ? lastMessageRef : null}
                  message={m}
                  onButtonClick={onButtonClick}
                  onMenuCommandClick={onMenuCommandClick}
                />
              </Box>
            ))}
          </Box>
          <ChatInputArea
            unitId={unit.externalIdentifier}
            sendMessage={sendMessage}
            menu={state.menu}
            onMenuCommandClick={onMenuCommandClick}
            inputRef={chatInputRef}
          />
        </>
      )}
      {isOpen && !isMobile && !isChatSplit && <ResizeHandle onResize={handleResize} />}
    </Drawer>
  );
};
